export const SAVE_ICON = {
  width: 24,
  height: 24,
  svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ><path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"/><path d="M7 3v4a1 1 0 0 0 1 1h7"/></svg>',
}

/**
 * Most pens a chart loads before asking. Chosen for legibility rather than a
 * measured cliff: beyond a few dozen lines the plot is unreadable, and cost
 * scales with pens x buckets (a 2,153-pen chart is ~21M points).
 */
export const MAX_PENS = 50

export const DEFAULT_CHART_SETTINGS = {
  RESAMPLE_MAX_POINTS: 5000,
  LEGEND_FONT_SIZE: 9,
  HOVER_FONT_SIZE: 11,
  TICK_FONT_SIZE: 9,
  FONT_COLOR: 'white',
  FONT_FAMILY: 'Manrope, sans-serif',
  TITLE_FONT_SIZE: 11,
  AXIS_TITLE_FONT_SIZE: 11,
  MAX_NAME_LENGTH: 30,
  NUM_REGRESSION_POINTS: 200,
} as const

/**
 * Limits on fetching pens at their true sample times instead of bucketed.
 * See `utils/rawFetchPlan.ts` for how these are applied; both numbers come from
 * measurements against dev, recorded here so they can be re-derived.
 */
export const RAW_FETCH_BUDGET = {
  /**
   * Most raw samples a pen may have, as a multiple of the buckets the aggregate
   * would have shipped for it anyway.
   *
   * The endpoints cost the same per point on the wire (measured: raw 137.3
   * B/point, aggregate 133.8 B/point), so this is close to "raw iff raw is no
   * more expensive than the status quo", with a deliberate premium: 25% more
   * wire is worth exact sample times and unaveraged values.
   *
   * The value is chosen so the reported case is actually fixed. `getInterval`
   * targets ~10,000 buckets at every window, and a 1/min pen over the 7-day
   * default has 11,701 real samples against 10,080 buckets -- 1.16x. At 1.0 the
   * complaint that prompted this work would still show `:00` timestamps.
   */
  MAX_POINTS_VS_BUCKETS: 1.25,

  /**
   * Ceiling on `raw points x pens on the chart`, which is what fetching raw
   * actually costs the main thread.
   *
   * Raw sample times do not align between pens -- two pens sampled a second
   * apart share no row -- so raw points become chart rows roughly 1:1 in
   * `mergeTimeSeriesResponse`. `generateUPlotData` then re-scans every row once
   * per pen, so cost grows with the product, not with either term alone. A flat
   * point budget gets this wrong in both directions at once: generous enough for
   * 50 pens is stingy for 3, and vice versa.
   *
   * Measured over a 7-day window on 13 real longridge pens: 72,893 raw points
   * (~948k product) cost 83ms of merge+render against 37ms for the all-aggregate
   * baseline. 1,000,000 buys that ~+46ms on a ~1.1s cold load and scales the
   * allowance the right way -- ~77k raw points at 13 pens, ~20k at the 50-pen
   * cap, and at 2 or 3 pens it never binds because MAX_POINTS_VS_BUCKETS does.
   */
  MAX_POINT_PEN_PRODUCT: 1_000_000,

  /**
   * Skip the count probe when only pens with an average gap above this many
   * seconds between samples could qualify for raw at all.
   *
   * The probe is a serialized COUNT round trip (~0.45s measured) paid before
   * the real fetch. Its per-pen verdict is capped by MAX_POINTS_VS_BUCKETS,
   * and that cap collapses on long windows: at 6 months with getInterval's
   * ~10,000 buckets, only a pen sampling less than once per ~21 minutes could
   * pass -- 200x sparser than the densest measured pen and well past the
   * sparsest -- so the round trip cannot change the outcome and is pure
   * latency.
   *
   * 300s puts the skip at ~43 days on a default 10,000-bucket window. Fewer
   * buckets (see BUCKETED_FETCH_BUDGET sharing them across pens) move the
   * cutoff proportionally earlier, which is right: the per-pen ceiling really
   * is lower. Past the cutoff a bucket is >= ~6 minutes wide on a >= 6-week
   * axis, where a bucket-start timestamp sits about a tenth of a pixel from
   * the true sample time -- the lie raw mode exists to fix is no longer
   * visible. Zooming back INTO a small window shrinks the qualifying gap and
   * the probe resumes automatically, so zoom-in keeps its raw upgrade.
   */
  SKIP_PROBE_ABOVE_GAP_SECONDS: 300,
} as const

/**
 * Bucket budget for a bucketed fetch, shared across the pens that ride it.
 *
 * `getInterval` used to hand every request its 10,000-point default regardless
 * of pen count, so 15 pens fetched ~150k points -- and the client resamples
 * every series down to RESAMPLE_MAX_POINTS (5,000) before drawing, so most of
 * that never reached a pixel. Points cost wire, parse and merge time
 * (mergeTimeSeriesResponse and generateUPlotData are both O(rows x pens)), so
 * the request budget is shared the way the cost is: each pen gets
 * TOTAL_POINTS / penCount buckets, clamped to
 * [MIN_POINTS_PER_PEN, MAX_POINTS_PER_PEN].
 *
 *  - MAX_POINTS_PER_PEN keeps small charts exactly as they were: getInterval's
 *    long-standing default, unchanged for 10 pens or fewer.
 *  - MIN_POINTS_PER_PEN equals RESAMPLE_MAX_POINTS, so even a 50-pen chart
 *    still ships at least as many buckets per pen as the resampler keeps.
 *    Render fidelity is unchanged at every pen count; only never-rendered
 *    points are dropped.
 *  - PEN_COUNT_QUANTUM: the pen count is rounded UP to the next multiple of
 *    this before the share is computed, so the budget only produces a few
 *    distinct per-pen values (10,000 / 6,666 / 5,000) instead of a new one
 *    for every pen count. Accepted tradeoff: the interval baked into a
 *    request depends on pen count, so crossing a quantum boundary (10 -> 11
 *    or 15 -> 16 pens) changes the interval and misses the fetch cache for
 *    the whole chart. Quantizing means adding/removing a pen *within* a step
 *    never flips the interval; a pen change already refetches (the entry_ids
 *    change), so the boundary case costs one full-window fetch, not an extra
 *    one. Rounding up (never down) keeps share x penCount <= TOTAL_POINTS.
 */
export const BUCKETED_FETCH_BUDGET = {
  TOTAL_POINTS: 100_000,
  MAX_POINTS_PER_PEN: 10_000,
  MIN_POINTS_PER_PEN: DEFAULT_CHART_SETTINGS.RESAMPLE_MAX_POINTS,
  PEN_COUNT_QUANTUM: 5,

  /**
   * Above this window, the per-pen budget drops to
   * LARGE_WINDOW_MAX_POINTS_PER_PEN regardless of pen count.
   *
   * Rationale: buckets on a large window are minutes-to-hours wide, so their
   * averages are the only fidelity anyone sees anyway (raw mode is long since
   * skipped, see RAW_FETCH_BUDGET.SKIP_PROBE_ABOVE_GAP_SECONDS), and the
   * client resamples every series to RESAMPLE_MAX_POINTS before drawing.
   * Fetching 10,000 buckets/pen for a year is therefore 2x wire, parse and
   * merge for zero pixels. 90 days keeps every existing window (7/30/60-day
   * defaults and tests) byte-identical; only genuinely huge windows (1y/5y
   * presets) get the cheaper fetch. The cap equals MIN_POINTS_PER_PEN, so it
   * composes with the pen share as a plain min and quantization still holds:
   * the interval depends only on the window and the pen-count quantum, never
   * on raw pen count, so cache keys stay stable.
   */
  LARGE_WINDOW_SECONDS: 90 * 86_400,
  LARGE_WINDOW_MAX_POINTS_PER_PEN: DEFAULT_CHART_SETTINGS.RESAMPLE_MAX_POINTS,
} as const

/**
 * Coarse-then-refine loading for large windows.
 *
 * Above MIN_WINDOW_SECONDS, a cold fetch first ships a cheap coarse pass
 * (~COARSE_POINTS_PER_PEN buckets/pen — enough to paint a legible line fast)
 * and then continues in the background to the full pen-scaled resolution for
 * the same window, replacing the coarse paint when it lands. 30 days is where
 * full-resolution responses start taking long enough (multi-second) that a
 * fast first paint is worth one extra small request; below it the full fetch
 * is already quick and two requests would just be overhead.
 */
export const TWO_PHASE_LOAD = {
  MIN_WINDOW_SECONDS: 30 * 86_400,
  COARSE_POINTS_PER_PEN: 1_200,
} as const

export const CHART_MARGINS = {
  DEFAULT_LEFT: 0,
  DEFAULT_RIGHT: 0,
  MULTIPLE_LEFT_AXES: 0,
  MULTIPLE_RIGHT_AXES: 0,
  SINGLE_AXIS_LEFT: 0,
  SINGLE_AXIS_RIGHT: 0,
  TOP: 0,
  BOTTOM: 40,
} as const

export const AXIS_DOMAIN_OFFSETS = {
  MULTIPLE_LEFT: 0.025,
  MULTIPLE_RIGHT: 0.975,
} as const

// Chart colors — design-system tokens so the plot tracks light/dark/zinc.
// CSS contexts can use these directly. Canvas / Plotly need
// `resolveChartColor()` because they cannot resolve `var(...)`.
export const CHART_COLORS = {
  BACKGROUND: 'var(--card)',
  TEXT: 'var(--foreground)',
  AXIS_STROKE: 'var(--muted-foreground)',
  GRID_LINE: 'var(--border)',
  TICK_STROKE: 'var(--border)',
  LEGEND_BORDER: 'var(--border)',
  LEGEND_BACKGROUND: 'color-mix(in oklab, var(--popover) 92%, transparent)',
  CURSOR_ACCENT: 'var(--chart-1)',
  CURSOR_ACCENT_LIGHT: 'color-mix(in oklab, var(--chart-1) 60%, transparent)',
  SELECTION_BG: 'color-mix(in oklab, var(--chart-1) 15%, transparent)',
  // Tooltip
  TOOLTIP_BG: 'var(--popover)',
  TOOLTIP_BORDER: 'var(--border)',
  TOOLTIP_TIME_COLOR: 'var(--muted-foreground)',
  TOOLTIP_NAME_COLOR: 'var(--muted-foreground)',
  TOOLTIP_VALUE_COLOR: 'var(--foreground)',
} as const

/**
 * Resolve a CSS color (including `var(--token)` / `color-mix(...)`) to a
 * concrete value canvas and Plotly can paint.
 */
export function resolveChartColor(color: string): string {
  if (typeof document === 'undefined') return color

  const probe = document.createElement('span')
  probe.style.color = color
  document.body.append(probe)
  const resolved = getComputedStyle(probe).color
  probe.remove()
  return resolved || color
}

// Common axis configuration defaults
export const AXIS_DEFAULTS = {
  gridcolor: 'transparent',
  ticklabeloverflow: 'hide past div',
  linewidth: 2,
  zeroline: false,
} as const

export const Y_AXIS_DEFAULTS = {
  ...AXIS_DEFAULTS,
  showline: false,
  tickmode: 'array', // Will be overridden when needed
} as const

export const X_AXIS_DEFAULTS = {
  ...AXIS_DEFAULTS,
  showline: true,
  spikesnap: 'cursor',
  // `linecolor` is set at Plotly layout call sites via
  // `resolveChartColor(CHART_COLORS.GRID_LINE)` — keep defaults concrete-only.
} as const

export const AXIS_TICK_FONT = {
  size: DEFAULT_CHART_SETTINGS.TICK_FONT_SIZE,
  family: DEFAULT_CHART_SETTINGS.FONT_FAMILY,
} as const

export const AXIS_TITLE_FONT = {
  size: DEFAULT_CHART_SETTINGS.AXIS_TITLE_FONT_SIZE,
  family: DEFAULT_CHART_SETTINGS.FONT_FAMILY,
} as const

export const UPLOT_TICK_FONT = `${DEFAULT_CHART_SETTINGS.TICK_FONT_SIZE}px ${DEFAULT_CHART_SETTINGS.FONT_FAMILY}`
export const UPLOT_TITLE_FONT = `${DEFAULT_CHART_SETTINGS.AXIS_TITLE_FONT_SIZE}px ${DEFAULT_CHART_SETTINGS.FONT_FAMILY}`
